import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { MeResponse, OrgRole } from '@grc/types';
import { apiFetch, ApiRequestError } from './api.js';
import { LoadingPane } from '../components/ui.js';

// ----------------------------------------------------------------- Auth
interface AuthState {
  me: MeResponse | null;
  loading: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
  /** Stop impersonating (platform admin) and reload as the real admin. */
  stopImpersonation: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['me'],
    queryFn: async (): Promise<MeResponse | null> => {
      try {
        return await apiFetch<MeResponse>('/auth/me');
      } catch (err) {
        if (err instanceof ApiRequestError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 60_000,
    retry: false,
  });

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['me'] });
  }, [queryClient]);

  const logout = useCallback(async () => {
    await apiFetch('/auth/logout', { method: 'POST' });
    queryClient.clear();
    window.location.href = '/login';
  }, [queryClient]);

  const stopImpersonation = useCallback(async () => {
    await apiFetch('/auth/impersonate/stop', { method: 'POST' });
    // Full reload so every cached org/user query is rebuilt as the admin.
    queryClient.clear();
    window.location.href = '/admin/orgs';
  }, [queryClient]);

  const value = useMemo<AuthState>(
    () => ({ me: data ?? null, loading: isLoading, refresh, logout, stopImpersonation }),
    [data, isLoading, refresh, logout, stopImpersonation],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}

// ------------------------------------------------------------------ Org
export interface CurrentOrg {
  orgId: string;
  orgName: string;
  orgSlug: string;
  plan: 'free' | 'paid';
  /** Member role, or 'assessor' when accessing via an assessor assignment. */
  role: OrgRole | 'assessor';
  /** Organisation AI switch: when false every AI affordance is hidden. */
  aiEnabled: boolean;
}

interface OrgState {
  org: CurrentOrg | null;
  orgs: CurrentOrg[];
  setOrgId: (orgId: string) => void;
}

const OrgContext = createContext<OrgState | null>(null);
const ORG_STORAGE_KEY = 'zest.currentOrgId';

export function OrgProvider({ children }: { children: ReactNode }) {
  const { me } = useAuth();
  const [selectedId, setSelectedId] = useState<string | null>(() =>
    localStorage.getItem(ORG_STORAGE_KEY),
  );

  const orgs = useMemo<CurrentOrg[]>(() => {
    if (!me) return [];
    const memberOrgs = me.memberships.map((m) => ({
      orgId: m.orgId,
      orgName: m.orgName,
      orgSlug: m.orgSlug,
      plan: m.plan,
      role: m.role as CurrentOrg['role'],
      aiEnabled: m.aiEnabled,
    }));
    const assessorOrgs = me.assessorOrgs
      .filter((a) => !memberOrgs.some((m) => m.orgId === a.orgId))
      // De-dup multiple assessment-scoped assignments to the same org.
      .filter((a, i, arr) => arr.findIndex((x) => x.orgId === a.orgId) === i)
      .map((a) => ({
        orgId: a.orgId,
        orgName: a.orgName,
        orgSlug: '',
        plan: 'free' as const,
        role: 'assessor' as const,
        aiEnabled: a.aiEnabled,
      }));
    return [...memberOrgs, ...assessorOrgs];
  }, [me]);

  const org = useMemo(() => {
    if (orgs.length === 0) return null;
    return orgs.find((o) => o.orgId === selectedId) ?? orgs[0] ?? null;
  }, [orgs, selectedId]);

  useEffect(() => {
    if (org) localStorage.setItem(ORG_STORAGE_KEY, org.orgId);
  }, [org]);

  const setOrgId = useCallback((orgId: string) => {
    localStorage.setItem(ORG_STORAGE_KEY, orgId);
    setSelectedId(orgId);
  }, []);

  return <OrgContext.Provider value={{ org, orgs, setOrgId }}>{children}</OrgContext.Provider>;
}

export function useOrg(): OrgState & { org: CurrentOrg } {
  const ctx = useContext(OrgContext);
  if (!ctx) throw new Error('useOrg must be used inside OrgProvider');
  if (!ctx.org) throw new Error('useOrg used outside an org-scoped route');
  return ctx as OrgState & { org: CurrentOrg };
}

export function useOrgOptional(): OrgState {
  const ctx = useContext(OrgContext);
  if (!ctx) throw new Error('useOrgOptional must be used inside OrgProvider');
  return ctx;
}

// ------------------------------------------------------------------ Guards
/** Redirect to /login when unauthenticated; to onboarding when org required but absent. */
export function RequireAuth({
  children,
  requireOrg = true,
}: {
  children: ReactNode;
  requireOrg?: boolean;
}) {
  const { me, loading } = useAuth();
  const { org } = useOrgOptional();
  const location = useLocation();

  if (loading) return <LoadingPane label="Signing you in" />;
  // Keep the query string: an invitation link carries its one-time token there.
  if (!me)
    return (
      <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />
    );
  // No workspace yet: someone who was invited should accept that invitation,
  // not be asked to found an organisation of their own.
  if (requireOrg && !org)
    return (
      <Navigate
        to={me.pendingInvitations.length > 0 ? '/invitations/accept' : '/onboarding/create-org'}
        replace
      />
    );
  return <>{children}</>;
}
